import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, Loader2, Truck, BookmarkPlus, Star } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useI18n, formatCAD } from "../i18n";
import { api, fileUrl } from "../lib/api";

const SWATCH = {
  white_vinyl: "bg-white",
  transparent_vinyl: "checkerboard",
  holographic_vinyl: "bg-gradient-to-br from-nbcyan via-nbpink to-nbyellow",
};

const CA_PROVINCES = [
  { code: "QC", en: "Quebec", fr: "Québec" },
  { code: "ON", en: "Ontario", fr: "Ontario" },
  { code: "BC", en: "British Columbia", fr: "Colombie-Britannique" },
  { code: "AB", en: "Alberta", fr: "Alberta" },
  { code: "MB", en: "Manitoba", fr: "Manitoba" },
  { code: "SK", en: "Saskatchewan", fr: "Saskatchewan" },
  { code: "NS", en: "Nova Scotia", fr: "Nouvelle-Écosse" },
  { code: "NB", en: "New Brunswick", fr: "Nouveau-Brunswick" },
  { code: "NL", en: "Newfoundland and Labrador", fr: "Terre-Neuve-et-Labrador" },
  { code: "PE", en: "Prince Edward Island", fr: "Île-du-Prince-Édouard" },
  { code: "NT", en: "Northwest Territories", fr: "Territoires du Nord-Ouest" },
  { code: "NU", en: "Nunavut", fr: "Nunavut" },
  { code: "YT", en: "Yukon", fr: "Yukon" },
];

const US_STATES = "AL,AK,AZ,AR,CA,CO,CT,DE,FL,GA,HI,ID,IL,IN,IA,KS,KY,LA,ME,MD,MA,MI,MN,MS,MO,MT,NE,NV,NH,NJ,NM,NY,NC,ND,OH,OK,OR,PA,RI,SC,SD,TN,TX,UT,VT,VA,WA,WV,WI,WY".split(",");

const SELECT_CLS = "w-full bg-white text-nbblack nb-border rounded-xl px-3 py-2 font-semibold outline-none focus:ring-2 focus:ring-nbpink cursor-pointer";

const fmtShipDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function Cart() {
  const { items, removeItem, total } = useCart();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState("CA");
  const [province, setProvince] = useState("");
  const [tax, setTax] = useState(null);
  const [ship, setShip] = useState({ full_name: "", email: "", phone: "", address1: "", address2: "", city: "", postal_code: "" });
  const setS = (k, v) => setShip((s) => ({ ...s, [k]: v }));
  const [addresses, setAddresses] = useState([]);
  const [saveLabel, setSaveLabel] = useState("");
  const [savingAddr, setSavingAddr] = useState(false);
  const [shipEst, setShipEst] = useState(null);

  const loadAddresses = useCallback(() => {
    if (!user) return;
    api.get("/me/addresses").then(({ data }) => setAddresses(data || [])).catch(() => {});
  }, [user]);

  // On load: fetch saved addresses; pre-fill from the default address if set,
  // otherwise fall back to the last address the customer used.
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      let addrs = [];
      try {
        const { data } = await api.get("/me/addresses");
        addrs = data || [];
      } catch (_) {}
      if (cancel) return;
      setAddresses(addrs);
      const def = addrs.find((a) => a.is_default);
      if (def) {
        applyAddress(def);
        return;
      }
      try {
        const { data } = await api.get("/me/last-shipping");
        if (!cancel && data && data.full_name) applyAddress(data);
      } catch (_) {}
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    api.get("/shipping/estimate").then(({ data }) => setShipEst(data)).catch(() => {});
  }, []);

  const applyAddress = (a) => {
    if (!a) return;
    setShip({
      full_name: a.full_name || "",
      email: a.email || user?.email || "",
      phone: a.phone || "",
      address1: a.address1 || "",
      address2: a.address2 || "",
      city: a.city || "",
      postal_code: a.postal_code || "",
    });
    if (a.country) setCountry(a.country);
    if (a.province) setProvince(a.province);
  };

  const saveAddress = async () => {
    setSavingAddr(true);
    try {
      const { data } = await api.post("/me/addresses", {
        ...ship,
        province: country === "CA" ? province : province,
        country,
        label: saveLabel,
      });
      setAddresses((a) => [...a, data]);
      setSaveLabel("");
      toast.success(t("cart.addressSaved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save address");
    } finally {
      setSavingAddr(false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      await api.delete(`/me/addresses/${id}`);
      setAddresses((a) => a.filter((x) => x.id !== id));
      toast.success(t("cart.addressDeleted"));
    } catch (e) {
      toast.error("Could not remove address");
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.put(`/me/addresses/${id}/default`);
      setAddresses((a) => a.map((x) => ({ ...x, is_default: x.id === id })));
      toast.success(t("cart.defaultSet"));
    } catch (e) {
      toast.error("Could not set default");
    }
  };

  useEffect(() => {
    if (user?.email) setShip((s) => (s.email ? s : { ...s, email: user.email }));
  }, [user]);

  const cartPayload = useCallback(
    () =>
      items.map((i) => ({
        sticker_id: i.sticker_id,
        preview_path: i.preview_path,
        name: i.name,
        width_in: Number(i.width_in),
        height_in: Number(i.height_in),
        material_key: i.material_key,
        cut_type: i.cut_type,
        shape: i.shape || "contour_cut",
        offset_in: i.offset_in,
        quantity: Number(i.quantity),
        message: i.message || "",
      })),
    [items]
  );

  const destinationReady = country === "US" || (country === "CA" && province);
  const contactReady = ship.full_name.trim() && (ship.email.trim() || user?.email) &&
    ship.address1.trim() && ship.city.trim() && ship.postal_code.trim();

  useEffect(() => {
    if (items.length === 0 || !destinationReady) {
      setTax(null);
      return;
    }
    let cancel = false;
    const run = async () => {
      try {
        const { data } = await api.post("/tax/quote", {
          items: cartPayload(),
          country,
          province: country === "CA" ? province : "",
        });
        if (!cancel) setTax(data);
      } catch (e) {
        if (!cancel) setTax(null);
      }
    };
    run();
    return () => {
      cancel = true;
    };
  }, [country, province, items, destinationReady, cartPayload]);

  const checkout = async () => {
    if (!user) {
      toast(t("cart.signInToCheckout"));
      navigate("/auth");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        country,
        province: country === "CA" ? province : "",
        shipping: {
          ...ship,
          province: country === "CA" ? province : province,
          country,
        },
        items: cartPayload(),
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const grandTotal = tax ? tax.total : total;

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-head font-extrabold text-4xl sm:text-5xl mb-8">{t("cart.title")}</h1>

        {items.length === 0 ? (
          <div className="bg-white nb-border nb-shadow rounded-3xl p-14 text-center">
            <ShoppingBag size={56} className="mx-auto mb-4 text-nbblack/30" />
            <p className="font-head font-bold text-xl mb-6">{t("cart.empty")}</p>
            <Link
              to="/design"
              data-testid="cart-empty-cta"
              className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press"
            >
              {t("cart.startDesigning")} <ArrowRight size={18} />
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {items.map((i) => (
                <div key={i.cartId} data-testid="cart-item" className="bg-white nb-border nb-shadow rounded-2xl p-4 flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-xl nb-border shrink-0 flex items-center justify-center overflow-hidden ${SWATCH[i.material_key] || "bg-white"}`}>
                    {i.preview_path && <img src={fileUrl(i.preview_path)} alt="" className="w-full h-full object-contain p-1" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-bold text-lg">{i.name}</p>
                    <p className="text-sm text-nbblack/60">
                      {i.material_name} · {i.cut_name} · {i.width_in}"×{i.height_in}"
                    </p>
                    <p className="text-sm font-semibold">
                      {i.cut_type === "sheet_cut" && i.total_stickers
                        ? `${i.quantity} ${Number(i.quantity) > 1 ? t("cart.sheets") : t("cart.sheet")} · ${i.total_stickers} ${t("cart.stickers")}`
                        : `${i.quantity} ${t("cart.pcs")} · ${formatCAD(i.unit_price)}/${t("cart.each")}`}
                    </p>
                    {i.message && <p className="text-xs text-nbblack/50 mt-1 italic truncate">“{i.message}”</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-head font-extrabold text-xl">{formatCAD(i.line_total)}</p>
                    <button
                      onClick={() => removeItem(i.cartId)}
                      data-testid="remove-item"
                      className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-red-600 hover:underline"
                    >
                      <Trash2 size={15} /> {t("cart.remove")}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-nbblack text-white nb-border nb-shadow-lg rounded-2xl p-6 sticky top-24">
                <h3 className="font-head font-extrabold text-xl mb-4">{t("cart.summary")}</h3>

                {/* Destination */}
                <div className="space-y-3 mb-5">
                  <p className="text-sm font-bold text-white/80">{t("cart.shipTo")}</p>
                  <select
                    data-testid="country-select"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setProvince("");
                    }}
                    className={SELECT_CLS}
                  >
                    <option value="CA">🇨🇦 {t("cart.canada")}</option>
                    <option value="US">🇺🇸 {t("cart.usa")}</option>
                  </select>
                  {country === "CA" ? (
                    <select data-testid="province-select" value={province} onChange={(e) => setProvince(e.target.value)} className={SELECT_CLS}>
                      <option value="">{t("cart.selectProvince")}</option>
                      {CA_PROVINCES.map((p) => (
                        <option key={p.code} value={p.code}>
                          {lang === "fr" ? p.fr : p.en}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select data-testid="state-select" value={province} onChange={(e) => setProvince(e.target.value)} className={SELECT_CLS}>
                      <option value="">{t("cart.selectState")}</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="space-y-2 mb-4" data-testid="shipping-form">
                  <h3 className="font-head font-bold text-sm text-white/80">{t("cart.shipTo")}</h3>
                  {user && addresses.length > 0 && (
                    <div className="space-y-1" data-testid="saved-addresses">
                      <select
                        data-testid="saved-address-select"
                        defaultValue=""
                        onChange={(e) => {
                          const a = addresses.find((x) => x.id === e.target.value);
                          applyAddress(a);
                        }}
                        className={SELECT_CLS}
                      >
                        <option value="">{t("cart.chooseSaved")}</option>
                        {addresses.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.is_default ? "★ " : ""}{a.label} — {a.full_name}, {a.city}
                          </option>
                        ))}
                      </select>
                      <div className="flex flex-col gap-1">
                        {addresses.map((a) => (
                          <div key={a.id} className="flex items-center gap-2 text-[11px]" data-testid={`address-chip-${a.id}`}>
                            <button
                              type="button"
                              onClick={() => makeDefault(a.id)}
                              disabled={a.is_default}
                              data-testid={`default-address-${a.id}`}
                              className={`inline-flex items-center gap-1 font-bold ${a.is_default ? "text-nbyellow" : "text-white/50 hover:text-nbyellow"}`}
                              title={a.is_default ? t("cart.defaultBadge") : t("cart.makeDefault")}
                            >
                              <Star size={12} className={a.is_default ? "fill-nbyellow" : ""} />
                              {a.is_default ? t("cart.defaultBadge") : t("cart.makeDefault")}
                            </button>
                            <span className="text-white/40">·</span>
                            <span className="font-bold text-white/60 truncate">{a.label}</span>
                            <button
                              type="button"
                              onClick={() => deleteAddress(a.id)}
                              data-testid={`delete-address-${a.id}`}
                              className="inline-flex items-center gap-1 font-bold text-white/50 hover:text-red-400 ml-auto"
                              title={`Remove ${a.label}`}
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <input data-testid="ship-name" value={ship.full_name} onChange={(e) => setS("full_name", e.target.value)} placeholder={t("cart.fullName")} className={SELECT_CLS} />
                  <input data-testid="ship-email" type="email" value={ship.email} onChange={(e) => setS("email", e.target.value)} placeholder={t("cart.email")} className={SELECT_CLS} />
                  <input data-testid="ship-phone" value={ship.phone} onChange={(e) => setS("phone", e.target.value)} placeholder={t("cart.phone")} className={SELECT_CLS} />
                  <input data-testid="ship-address1" value={ship.address1} onChange={(e) => setS("address1", e.target.value)} placeholder={t("cart.address1")} className={SELECT_CLS} />
                  <input data-testid="ship-address2" value={ship.address2} onChange={(e) => setS("address2", e.target.value)} placeholder={t("cart.address2")} className={SELECT_CLS} />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="ship-city" value={ship.city} onChange={(e) => setS("city", e.target.value)} placeholder={t("cart.city")} className={SELECT_CLS} />
                    <input data-testid="ship-postal" value={ship.postal_code} onChange={(e) => setS("postal_code", e.target.value)} placeholder={t("cart.postal")} className={SELECT_CLS} />
                  </div>
                  {user && contactReady && (
                    <div className="flex items-center gap-2 pt-1" data-testid="save-address-row">
                      <input
                        data-testid="save-address-label"
                        value={saveLabel}
                        onChange={(e) => setSaveLabel(e.target.value)}
                        placeholder={t("cart.addressLabelPh")}
                        className={SELECT_CLS + " flex-1"}
                      />
                      <button
                        type="button"
                        onClick={saveAddress}
                        disabled={savingAddr}
                        data-testid="save-address-btn"
                        className="inline-flex items-center gap-1.5 bg-nbcyan text-nbblack font-head font-bold text-sm px-3 py-2 rounded-xl nb-border nb-press disabled:opacity-50 whitespace-nowrap"
                      >
                        {savingAddr ? <Loader2 className="animate-spin" size={15} /> : <BookmarkPlus size={15} />}
                        {t("cart.saveThisAddress")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-between text-white/70 mb-2">
                  <span>{t("cart.subtotal")}</span>
                  <span data-testid="cart-subtotal">{formatCAD(tax ? tax.subtotal : total)}</span>
                </div>
                <div className="flex justify-between text-white/70 mb-2">
                  <span>{t("cart.shipping")}</span>
                  <span className="font-bold text-nbcyan">{t("cart.free")}</span>
                </div>
                {shipEst?.ship_date && (
                  <div className="flex items-center gap-2 mb-3 bg-white/10 rounded-xl px-3 py-2" data-testid="ships-by-badge">
                    <Truck size={16} className="text-nbcyan shrink-0" />
                    <span className="text-sm font-bold">
                      {t("cart.shipsBy")} <span className="text-nbcyan" data-testid="ships-by-date">{fmtShipDate(shipEst.ship_date)}</span>
                    </span>
                  </div>
                )}

                {tax && tax.tax_lines.length > 0 && (
                  <div className="space-y-2 mb-2" data-testid="tax-breakdown">
                    {tax.tax_lines.map((l) => (
                      <div key={l.code}>
                        <div className="flex justify-between text-white/70">
                          <span>
                            {lang === "fr" ? l.label_fr : l.label} ({(l.rate * 100).toFixed(l.code === "QST" ? 3 : 0)}%)
                          </span>
                          <span data-testid={`tax-${l.code}`}>{formatCAD(l.amount)}</span>
                        </div>
                        <div className="text-[10px] text-white/40 leading-tight">{l.number}</div>
                      </div>
                    ))}
                  </div>
                )}

                {country === "US" && tax && (
                  <p className="text-xs text-white/40 mb-2" data-testid="us-no-tax">
                    {t("cart.noTaxUS")}
                  </p>
                )}
                {!destinationReady && <p className="text-xs text-white/40 mb-2">{t("cart.chooseDestination")}</p>}

                <div className="flex justify-between items-end border-t border-white/20 pt-4 mb-6">
                  <span className="font-head font-bold">{t("cart.total")}</span>
                  <span data-testid="cart-total" className="font-head font-extrabold text-3xl">
                    {formatCAD(grandTotal)}
                  </span>
                </div>

                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={busy || !destinationReady || !contactReady}
                  className="w-full bg-nbpink text-white font-head font-bold text-lg py-3 rounded-full nb-border nb-press disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="animate-spin" size={20} /> : <>{t("cart.checkout")} <ArrowRight size={18} /></>}
                </button>
                <p className="text-xs text-white/40 mt-3 text-center">{t("cart.securePay")}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
